import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar, Image, Mic, FileText, X, CalendarIcon, Search, Download, Video, User } from "lucide-react";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { DateRange } from "react-day-picker";
import { useLanguage } from "@/contexts/LanguageContext";

interface FilterBarProps {
  activeFilter: string | null;
  onFilterChange: (filter: string | null) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  dateRange: DateRange | undefined;
  onDateRangeChange: (range: DateRange | undefined) => void;
  onExportPDF: () => void;
  hideExport?: boolean;
  onOpenExportDialog?: () => void;
}

const FilterBar = ({ activeFilter, onFilterChange, searchQuery, onSearchChange, dateRange, onDateRangeChange, onExportPDF, hideExport = false, onOpenExportDialog }: FilterBarProps) => {
  const { t } = useLanguage();
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const filters = [
    { id: "images", label: t("viewer.images"), icon: Image, shortLabel: t("viewer.img") },
    { id: "audio", label: t("viewer.audio"), icon: Mic, shortLabel: t("viewer.audio") },
    { id: "documents", label: t("viewer.documents"), icon: FileText, shortLabel: t("viewer.docs") },
    { id: "videos", label: t("viewer.videos"), icon: Video, shortLabel: t("viewer.videos") },
    { id: "contacts", label: t("viewer.contacts"), icon: User, shortLabel: t("viewer.contacts") },
  ];

  const clearDateFilter = () => {
    onDateRangeChange(undefined);
  };

  return (
    <div className="bg-card border-b shadow-soft">
      <div className="container mx-auto px-3 sm:px-4 py-2 sm:py-3">
        {/* Search Bar */}
        <div className="mb-3 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder={t("viewer.searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10 py-2 text-sm transition-smooth focus:shadow-soft"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onSearchChange("")}
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs sm:text-sm font-medium text-muted-foreground mr-1 sm:mr-2">{t("viewer.filter")}</span>
            
            {/* Date Range Picker */}
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant={dateRange?.from ? "default" : "outline"}
                  size="sm"
                  className={cn(
                    "transition-smooth text-xs sm:text-sm px-2 sm:px-3",
                    dateRange?.from && "gradient-primary text-white shadow-elegant"
                  )}
                >
                  <CalendarIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="hidden xs:inline">
                    {dateRange?.from ? (
                      dateRange.to ? (
                        `${format(dateRange.from, "MMM d")} - ${format(dateRange.to, "MMM d")}`
                      ) : (
                        format(dateRange.from, "MMM d, yyyy")
                      )
                    ) : (
                      t("viewer.dateRange")
                    )}
                  </span>
                  <span className="xs:hidden">{t("viewer.date")}</span>
                  {dateRange?.from && (
                    <X 
                      className="w-3 h-3 sm:w-4 sm:h-4 ml-1 sm:ml-2" 
                      onClick={(e) => {
                        e.stopPropagation();
                        clearDateFilter();
                      }}
                    />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="range"
                  selected={dateRange}
                  onSelect={onDateRangeChange}
                  initialFocus
                  numberOfMonths={2}
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>

            {filters.map((filter) => {
              const Icon = filter.icon;
              const isActive = activeFilter === filter.id;
              
              return (
                <Button
                  key={filter.id}
                  variant={isActive ? "default" : "outline"}
                  size="sm"
                  onClick={() => onFilterChange(isActive ? null : filter.id)}
                  className={cn(
                    "transition-smooth text-xs sm:text-sm px-2 sm:px-3",
                    isActive && "gradient-primary text-white shadow-elegant"
                  )}
                >
                  <Icon className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="hidden xs:inline">{filter.label}</span>
                  <span className="xs:hidden">{filter.shortLabel}</span>
                  {isActive && <X className="w-3 h-3 sm:w-4 sm:h-4 ml-1 sm:ml-2" />}
                </Button>
              );
            })}
          </div>

          {/* Export PDF Button */}
          {!hideExport && (
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-2 text-xs sm:text-sm px-2 sm:px-3"
              onClick={onOpenExportDialog || onExportPDF}
            >
              <Download className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden xs:inline">{t("viewer.exportPdf")}</span>
              <span className="xs:hidden">{t("viewer.pdf")}</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilterBar;
